import { useState, useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { 
  FileText,
  Download,
  Printer,
  Save,
  Filter,
  Info,
  CheckCircle,
  XCircle,
  AlertCircle,
  Calendar,
  TrendingUp,
  DollarSign,
  Building,
  Users,
  Zap,
  MapPin,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { format, subMonths, startOfMonth, eachMonthOfInterval } from 'date-fns';
import { InvoiceParticipationHeatmap } from './components/InvoiceParticipationHeatmap';
import { MissingInvoiceData } from './components/MissingInvoiceData';
import { LateFeesView } from './components/LateFeesView';
import { LateBillsView } from './components/LateBillsView';
import { AnalyticsOverview } from './components/AnalyticsOverview';

export default function TumAnalytics() {
  const { currentWorkspace } = useWorkspace();
  const [activeReport, setActiveReport] = useState('overview');
  const [activeHealthTab, setActiveHealthTab] = useState('invoice-participation');
  
  // Filters
  const [dateFilter, setDateFilter] = useState('2025');
  const [utilityTypeFilter, setUtilityTypeFilter] = useState('all');
  const [billTypeFilter, setBillTypeFilter] = useState('all');
  const [locationFilter, setLocationFilter] = useState('all');
  const [vendorFilter, setVendorFilter] = useState('all');
  const [accountStatusFilter, setAccountStatusFilter] = useState('all');

  const reportTabs = [
    { id: 'overview', label: 'Overview', icon: TrendingUp },
    { id: 'bill-health', label: 'Bill Health Report', icon: FileText },
  ];

  const healthTabs = [
    { id: 'invoice-participation', label: 'Invoice Participation' },
    { id: 'missing-invoice-data', label: 'Missing Invoice Data' },
    { id: 'notifications', label: 'Notifications' },
    { id: 'warnings-overview', label: 'Warnings Overview' },
    { id: 'warnings-over-time', label: 'Warnings over Time' },
    { id: 'impacted-locations', label: 'Impacted Locations' },
    { id: 'late-fees', label: 'Late Fees' },
    { id: 'late-bills', label: 'Late Bills' },
    { id: 'received-bills-details', label: 'Received Bills Details' },
  ];

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold tracking-tight">
            Analytics {currentWorkspace?.name || ''}
          </h1>
          <p className="text-muted-foreground mt-2 text-lg">
            Comprehensive utility analytics and bill health monitoring
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" className="gap-2">
            <Printer className="h-4 w-4" />
            Print View
          </Button>
          <Button variant="outline" size="sm" className="gap-2">
            <Save className="h-4 w-4" />
            Save View
          </Button>
          <Select defaultValue="main-analytics">
            <SelectTrigger className="w-[200px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="main-analytics">Main Analytics Report</SelectItem>
              <SelectItem value="bill-health">Bill Health Report</SelectItem>
              <SelectItem value="consumption">Consumption Report</SelectItem>
              <SelectItem value="charges">Charges Report</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Filters */}
      <Card className="border-l-4 border-l-primary/50">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Filter className="h-5 w-5 text-primary" />
            </div>
            <div>
              <CardTitle className="text-lg">Filters</CardTitle>
              <CardDescription>Refine your analytics view</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Date</label>
              <Select value={dateFilter} onValueChange={setDateFilter}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="2025">2025</SelectItem>
                  <SelectItem value="2024">2024</SelectItem>
                  <SelectItem value="last-12-months">Last 12 Months</SelectItem>
                  <SelectItem value="last-13-months">Last 13 Months</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Utility Type</label>
              <Select value={utilityTypeFilter} onValueChange={setUtilityTypeFilter}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="electric">Electric</SelectItem>
                  <SelectItem value="water">Water</SelectItem>
                  <SelectItem value="sewer">Sewer</SelectItem>
                  <SelectItem value="gas">Natural Gas</SelectItem>
                  <SelectItem value="refuse">Refuse</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Bill Type</label>
              <Select value={billTypeFilter} onValueChange={setBillTypeFilter}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="full-service">Full Service</SelectItem>
                  <SelectItem value="supply-only">Supply Only</SelectItem>
                  <SelectItem value="distribution-only">Distribution Only</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Location</label>
              <Select value={locationFilter} onValueChange={setLocationFilter}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="location-1">Location 1</SelectItem>
                  <SelectItem value="location-2">Location 2</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Vendor</label>
              <Select value={vendorFilter} onValueChange={setVendorFilter}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="duke-energy">Duke Energy</SelectItem>
                  <SelectItem value="water-works">Water Works</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Account Status</label>
              <Select value={accountStatusFilter} onValueChange={setAccountStatusFilter}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="open">Open</SelectItem>
                  <SelectItem value="closed">Closed</SelectItem>
                  <SelectItem value="multiple">Multiple selections</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Main Report Tabs */}
      <Tabs value={activeReport} onValueChange={setActiveReport} className="w-full">
        <div className="border-b border-border">
          <TabsList className="w-full h-auto p-0 bg-transparent justify-start overflow-x-auto flex-nowrap gap-1">
            {reportTabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <TabsTrigger
                  key={tab.id}
                  value={tab.id}
                  className="flex items-center gap-2 px-4 py-3 text-sm font-medium rounded-t-lg border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-background data-[state=active]:shadow-sm transition-all whitespace-nowrap"
                >
                  <Icon className="h-4 w-4" />
                  {tab.label}
                </TabsTrigger>
              );
            })}
          </TabsList>
        </div>

        <TabsContent value="overview" className="mt-6">
          <AnalyticsOverview 
            dateFilter={dateFilter}
            utilityTypeFilter={utilityTypeFilter}
            billTypeFilter={billTypeFilter}
            locationFilter={locationFilter}
          />
        </TabsContent>

        <TabsContent value="bill-health" className="mt-6">
          {/* Bill Health Report Tabs */}
          <Tabs value={activeHealthTab} onValueChange={setActiveHealthTab} className="w-full">
            <div className="border-b border-border mb-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <FileText className="h-5 w-5 text-primary" />
                  <h2 className="text-2xl font-bold">Bill Health Report</h2>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" className="gap-2">
                    <Download className="h-4 w-4" />
                    Export
                  </Button>
                </div>
              </div>
              <div className="flex items-center gap-1 overflow-x-auto pb-2">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0"
                  onClick={() => {
                    const currentIndex = healthTabs.findIndex(t => t.id === activeHealthTab);
                    if (currentIndex > 0) {
                      setActiveHealthTab(healthTabs[currentIndex - 1].id);
                    }
                  }}
                  disabled={healthTabs.findIndex(t => t.id === activeHealthTab) === 0}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <TabsList className="w-full h-auto p-0 bg-transparent justify-start overflow-x-auto flex-nowrap gap-1">
                  {healthTabs.map((tab) => (
                    <TabsTrigger
                      key={tab.id}
                      value={tab.id}
                      className="flex items-center gap-2 px-3 py-2 text-xs font-medium rounded-md border border-transparent data-[state=active]:border-primary data-[state=active]:bg-primary/10 data-[state=active]:text-primary transition-all whitespace-nowrap"
                    >
                      {tab.label}
                    </TabsTrigger>
                  ))}
                </TabsList>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0"
                  onClick={() => {
                    const currentIndex = healthTabs.findIndex(t => t.id === activeHealthTab);
                    if (currentIndex < healthTabs.length - 1) {
                      setActiveHealthTab(healthTabs[currentIndex + 1].id);
                    }
                  }}
                  disabled={healthTabs.findIndex(t => t.id === activeHealthTab) === healthTabs.length - 1}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>

            <TabsContent value="invoice-participation" className="mt-0">
              <InvoiceParticipationHeatmap
                dateFilter={dateFilter}
                utilityTypeFilter={utilityTypeFilter}
                billTypeFilter={billTypeFilter}
                locationFilter={locationFilter}
                vendorFilter={vendorFilter}
                accountStatusFilter={accountStatusFilter}
              />
            </TabsContent>

            <TabsContent value="missing-invoice-data" className="mt-0">
              <MissingInvoiceData
                dateFilter={dateFilter}
                utilityTypeFilter={utilityTypeFilter}
                locationFilter={locationFilter}
                vendorFilter={vendorFilter}
              />
            </TabsContent>

            <TabsContent value="late-fees" className="mt-0">
              <LateFeesView
                dateFilter={dateFilter}
                utilityTypeFilter={utilityTypeFilter}
                locationFilter={locationFilter}
              />
            </TabsContent>

            <TabsContent value="late-bills" className="mt-0">
              <LateBillsView
                dateFilter={dateFilter}
                utilityTypeFilter={utilityTypeFilter}
                locationFilter={locationFilter}
                accountStatusFilter={accountStatusFilter}
              />
            </TabsContent>

            {/* Placeholder tabs */}
            <TabsContent value="notifications" className="mt-0">
              <Card>
                <CardHeader>
                  <CardTitle>Notifications</CardTitle>
                  <CardDescription>Bill health notifications and alerts</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">Notifications view coming soon...</p>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="warnings-overview" className="mt-0">
              <Card>
                <CardHeader>
                  <CardTitle>Warnings Overview</CardTitle>
                  <CardDescription>Summary of bill health warnings</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">Warnings overview coming soon...</p>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="warnings-over-time" className="mt-0">
              <Card>
                <CardHeader>
                  <CardTitle>Warnings over Time</CardTitle>
                  <CardDescription>Trend analysis of warnings</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">Warnings over time coming soon...</p>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="impacted-locations" className="mt-0">
              <Card>
                <CardHeader>
                  <CardTitle>Impacted Locations</CardTitle>
                  <CardDescription>Locations with bill health issues</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">Impacted locations coming soon...</p>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="received-bills-details" className="mt-0">
              <Card>
                <CardHeader>
                  <CardTitle>Received Bills Details</CardTitle>
                  <CardDescription>Detailed view of received bills</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">Received bills details coming soon...</p>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </TabsContent>
      </Tabs>
    </div>
  );
}
